import { api } from "./client";

/** Skills (decision 0035): the shapes the API answers and takes. */
export type SkillArea =
  | "essentials"
  | "sales"
  | "marketing"
  | "support"
  | "success"
  | "retention"
  | "finance"
  | "people"
  | "operations";
export type SkillLocale = "pt-BR" | "es" | "en";
export type Localized = Record<SkillLocale, string>;

export interface SkillText {
  name: string;
  summary: string;
  whenToUse: string;
  goal: string;
  steps: string[];
  always: string[];
  never: string[];
  examples: Array<{ contact: string; reply: string }>;
  origin: string;
}
export interface SetupQuestion {
  key: string;
  kind: "text" | "choice" | "number" | "boolean";
  label: Localized;
  help?: Localized;
  options?: Localized[];
  /** The answer that holds until the company answers (a choice's index for "choice"). */
  default?: string | number | boolean;
}
export interface LibrarySkill {
  slug: string;
  version: number;
  changes?: Localized;
  area: SkillArea;
  audience: "customers" | "team";
  market: string;
  method: string;
  text: Record<SkillLocale, SkillText>;
  setup: SetupQuestion[];
  brings: {
    fields: Array<{
      key: string;
      type: string;
      label: Localized;
      hint: Localized;
      options?: Localized[];
    }>;
    handoff: Localized[];
    outcomes: Array<{ code: string; label: Localized }>;
  };
  scenarios: Array<{
    key: string;
    title: Localized;
    persona: Localized;
    messages: Localized[];
    expect: Localized[];
  }>;
}
export interface LibraryCard {
  slug: string;
  area: SkillArea;
  audience: "customers" | "team";
  market: string;
  method: string;
  version: number;
  name: string;
  summary: string;
  whenToUse: string;
  addedId: string | null;
}
export type SkillAnswers = Record<string, string | number | boolean | null>;
export interface SkillContent {
  goal: string;
  steps: string[];
  always: string[];
  never: string[];
  examples: Array<{ contact: string; reply: string }>;
  notes: string;
}
export interface SkillLinks {
  handoff: string[];
  fields: string[];
  toolIds: string[];
  collectionIds: string[];
}
export interface SkillRow {
  id: string;
  key: string;
  kind: "library" | "company";
  catalogSlug: string | null;
  catalogVersion: number | null;
  latestVersion: number | null;
  updateAvailable: boolean;
  name: string | null;
  summary: string | null;
  area: SkillArea | null;
  audience: "customers" | "team";
  method: string | null;
  market: string | null;
  whenToUse: string | null;
  content: SkillContent;
  setupAnswers: SkillAnswers;
  links: SkillLinks;
  origin: "manual" | "library" | "copy" | "organize";
  revision: number;
  updatedAt: string;
  usedBy: string[];
}
export interface CompanySkillBody {
  name: string;
  area: SkillArea;
  audience: "customers" | "team";
  whenToUse: string;
  content: SkillContent;
  links: SkillLinks;
}
/** How a version of an assistant uses a skill. */
export interface AssistantSkill {
  skillId: string;
  mode: "on_demand" | "always";
  whenToUse?: string;
}

export interface AiQuestion {
  id: string;
  topic: string;
  question: string;
  /** The first one is the recommended answer. */
  options: Array<{ label: string; description: string }>;
}
export interface OrganizeProposal {
  summary: string;
  instructions: { text: string; why: string } | null;
  knowledge: Array<{
    id: string;
    fact: string;
    status: "new" | "present" | "outdated";
    note: string;
  }>;
  companySkills: Array<{
    id: string;
    name: string;
    area: SkillArea;
    mode: "on_demand" | "always";
    whenToUse: string;
    goal: string;
    steps: string[];
    always: string[];
    never: string[];
    examples: Array<{ contact: string; reply: string }>;
    handoff: string[];
  }>;
  librarySkills: Array<{
    slug: string;
    mode: "on_demand" | "always";
    answers: SkillAnswers;
    why: string;
  }>;
  fields: Array<{
    id: string;
    action: "create" | "fix";
    key: string;
    label: string;
    type: string;
    options: string[];
    hint: string;
    why: string;
  }>;
  handoff: string[];
}
export interface OrganizeRun {
  id: string;
  assistantId: string | null;
  status: "running" | "questions" | "ready" | "applied" | "failed";
  chars: number;
  proposal: OrganizeProposal | null;
  rounds: AiQuestion[][];
  answers: Record<string, string>;
  error: string | null;
  applied: {
    skills: string[];
    fields: string[];
    facts: number;
    draftVersionId: string | null;
  } | null;
}
export interface OrganizeAccept {
  instructions: boolean;
  knowledge: string[];
  collectionId: string | null;
  companySkills: string[];
  librarySkills: string[];
  fields: string[];
  handoff: boolean;
}

export interface Scenario {
  id: string;
  source: "library" | "company";
  skill: string | null;
  title: string;
  persona: string;
  messages: string[];
  expect: string[];
  lastResult: { passed: number; total: number; at: string } | null;
}
export interface ScenarioResult {
  replies: string[];
  actions: Array<{ kind: string; [k: string]: unknown }>;
  results: Array<{ expect: string; pass: boolean; why: string }>;
  passed: number;
  total: number;
}

export interface AssistantMemory {
  model: string;
  window: number;
  parts: Array<{
    key: "rules" | "tools" | "skills" | "knowledge" | "conversation";
    tokens: number;
  }>;
  total: number;
  share: number;
  knowledge: Array<{
    id: string;
    name: string;
    chars: number;
    mode: "auto" | "whole" | "search";
    whole: boolean;
  }>;
  costPerReply: {
    usdMicros: number;
    brlMicros: number | null;
    fxDay: string | null;
  } | null;
}

/** "Como a IA respondeu": the skills, knowledge, tools and context behind a reply. */
export interface ReplyTrace {
  /** Which model answered, with what it took and any it fell back from (decision 0037). */
  model?: {
    provider: string;
    model: string;
    settings?: { effort?: string; temperature?: number; maxTokens?: number };
    fallbacks?: Array<{ provider: string; model: string; reason: string }>;
  };
  /** How long the reply took, from reading the conversation to sending. */
  ms?: number;
  /** The assistant's version whose instructions wrote it. */
  version?: number;
  /** A follow-up the assistant wrote while the contact was quiet (decision 0044). */
  followUp?: { n: number; of: number };
  skills: Array<{ name: string; when: "always" | "earlier" | "here" }>;
  knowledge: { whole: string[]; passages: string[] };
  tools: string[];
  context: {
    messages: number;
    previous: number;
    inputTokens: number | null;
    cachedTokens: number | null;
  };
}

const base = (slug: string) => `/v1/c/${slug}`;

export const skillsApi = {
  list: (slug: string, locale: string) =>
    api<{ skills: SkillRow[] }>(
      `${base(slug)}/skills?locale=${encodeURIComponent(locale)}`,
    ),
  library: (slug: string, locale: string, all = false) =>
    api<{
      market: string;
      areas: SkillArea[];
      skills: LibraryCard[];
      /** Suggested by the setup questionnaire's purposes and segment. */
      suggested: string[];
    }>(
      `${base(slug)}/skills/library?locale=${encodeURIComponent(locale)}${all ? "&all=1" : ""}`,
    ),
  librarySkill: (slug: string, skillSlug: string) =>
    api<{
      skill: LibrarySkill;
      pinned: LibrarySkill | null;
      adoption: {
        id: string;
        version: number;
        answers: SkillAnswers;
        declinedVersion: number | null;
      } | null;
      existingFields: string[];
      existingOutcomes: string[];
    }>(`${base(slug)}/skills/library/${encodeURIComponent(skillSlug)}`),
  add: (
    slug: string,
    skillSlug: string,
    body: {
      answers: SkillAnswers;
      createFields: string[];
      createOutcomes: string[];
      bringAll?: boolean;
    },
  ) =>
    api<{ skill: SkillRow }>(
      `${base(slug)}/skills/library/${encodeURIComponent(skillSlug)}`,
      { method: "POST", body },
    ),
  get: (slug: string, id: string, locale: string) =>
    api<{ skill: SkillRow; preview: string }>(
      `${base(slug)}/skills/${id}?locale=${encodeURIComponent(locale)}`,
    ),
  create: (slug: string, body: CompanySkillBody) =>
    api<{ skill: SkillRow }>(`${base(slug)}/skills`, { method: "POST", body }),
  save: (slug: string, id: string, body: CompanySkillBody) =>
    api<{ skill: SkillRow }>(`${base(slug)}/skills/${id}`, {
      method: "PUT",
      body,
    }),
  saveAnswers: (slug: string, id: string, answers: SkillAnswers) =>
    api<{ skill: SkillRow }>(`${base(slug)}/skills/${id}`, {
      method: "PUT",
      body: { answers },
    }),
  update: (slug: string, id: string, accept: boolean) =>
    api<{ skill: SkillRow }>(`${base(slug)}/skills/${id}/update`, {
      method: "POST",
      body: { accept },
    }),
  copy: (slug: string, id: string, locale: string) =>
    api<{ skill: SkillRow }>(
      `${base(slug)}/skills/${id}/copy?locale=${encodeURIComponent(locale)}`,
      { method: "POST", body: {} },
    ),
  remove: (slug: string, id: string) =>
    api<{ ok: true }>(`${base(slug)}/skills/${id}`, { method: "DELETE" }),
  revisions: (slug: string, id: string) =>
    api<{
      revisions: Array<{
        revision: number;
        created_at: string;
        author: string | null;
      }>;
    }>(`${base(slug)}/skills/${id}/revisions`),

  organize: (
    slug: string,
    body: { text: string; assistantId: string | null; locale: string },
  ) =>
    api<{ runId: string }>(`${base(slug)}/skills/organize`, {
      method: "POST",
      body,
    }),
  organizeRun: (slug: string, runId: string) =>
    api<{ run: OrganizeRun }>(`${base(slug)}/skills/organize/${runId}`),
  organizeAnswers: (
    slug: string,
    runId: string,
    answers: Record<string, string>,
    locale: string,
  ) =>
    api<{ ok: true }>(`${base(slug)}/skills/organize/${runId}/answers`, {
      method: "POST",
      body: { answers, locale },
    }),
  organizeApply: (slug: string, runId: string, body: OrganizeAccept) =>
    api<{
      skills: string[];
      fields: string[];
      facts: number;
      collectionId: string | null;
      draftVersionId: string | null;
    }>(`${base(slug)}/skills/organize/${runId}/apply`, {
      method: "POST",
      body,
    }),

  scenarios: (
    slug: string,
    body: {
      assistantId: string | null;
      skills: AssistantSkill[];
      locale: string;
    },
  ) =>
    api<{ scenarios: Scenario[] }>(`${base(slug)}/scenarios/list`, {
      method: "POST",
      body,
    }),
  createScenario: (
    slug: string,
    body: {
      assistantId: string | null;
      title: string;
      persona: string;
      messages: string[];
      expect: string[];
    },
  ) => api<{ id: string }>(`${base(slug)}/scenarios`, { method: "POST", body }),
  removeScenario: (slug: string, id: string) =>
    api<{ ok: true }>(`${base(slug)}/scenarios/${id}`, { method: "DELETE" }),
  runScenario: (
    slug: string,
    body: {
      draft: unknown;
      scenario: Pick<Scenario, "title" | "persona" | "messages" | "expect">;
      scenarioId: string | null;
      locale: string;
    },
  ) =>
    api<ScenarioResult>(`${base(slug)}/scenarios/run`, {
      method: "POST",
      body,
    }),

  memory: (slug: string, assistantId: string, draft: unknown) =>
    api<{ memory: AssistantMemory }>(
      `${base(slug)}/assistants/${assistantId}/memory`,
      { method: "POST", body: draft },
    ),
  trace: (slug: string, conversationId: string, messageId: string) =>
    api<{ trace: ReplyTrace | null }>(
      `${base(slug)}/conversations/${conversationId}/messages/${messageId}/trace`,
    ),
};
